// useEventValidation.js - Custom hook for event form validation
import { useMemo, useCallback } from "react";
import { estimateCaptionBudget, MAX_CAPTION_LENGTH } from "../../helpers/captionBudget";

/**
 * Validation rules for event form fields
 * Each field has: required (boolean), validator (function), message (string), step (number)
 */
const VALIDATION_RULES = {
  // Step 1: Event Details
  eventType: {
    required: true,
    step: 1,
    field: "eventType",
    validator: (v) => v && v.trim() !== "",
    message: "Event type is required",
  },
  eventName: {
    required: true,
    step: 1,
    field: "eventName",
    validator: (v) => v && v.trim() !== "",
    message: "Event name is required",
  },
  description: {
    required: true,
    step: 1,
    field: "description",
    validator: (v) => v && v.trim() !== "",
    message: "Event description is required",
  },
  termsConditions: {
    required: true,
    step: 1,
    field: "termsConditions",
    validator: (v) => v && v.trim() !== "",
    message: "Terms & Conditions are required",
  },
  instructions: {
    required: true,
    step: 1,
    field: "instructions",
    validator: (v) => v && v.trim() !== "",
    message: "Instructions are required",
  },
  startDate: {
    required: true,
    step: 1,
    field: "startDate",
    validator: (v) => v && v.trim() !== "",
    message: "Start date is required",
  },
  endDate: {
    required: true,
    step: 1,
    field: "endDate",
    validator: (v) => v && v.trim() !== "",
    message: "End date is required",
  },
  votingDuration: {
    required: false,
    step: 1,
    field: "votingDuration",
    validator: (v) => v !== undefined && v !== null,
    message: "Voting duration is recommended",
  },

  // Step 2: Additional Details
  eventCaption1: {
    required: false,
    step: 2,
    field: "eventCaption1",
    getValueFromData: (formData) => formData?.additionalData?.eventCaption1,
    validator: (v) => v && v.trim() !== "",
    message: "Participation post caption is optional",
  },
  eventCaption2: {
    // Item 7 (2026-06-04): the Event Referral Caption is now an OPTIONAL
    // WhatsApp/Telegram footer (no longer part of the Instagram post), so it
    // must not block publishing. Was required: true.
    required: false,
    step: 2,
    field: "eventCaption2",
    getValueFromData: (formData) => formData?.additionalData?.eventCaption2,
    validator: () => true,
    message: "Event Referral Caption is optional (WhatsApp/Telegram footer)",
  },
  eventHashtags: {
    required: false,
    step: 2,
    field: "eventHashtags",
    getValueFromData: (formData) => formData?.additionalData?.eventHashtags,
    validator: () => true,
    message: "Event hashtags (moved to Participation tab)",
  },
  eventPrice: {
    required: false,
    step: 2,
    field: "eventPrice",
    getValueFromData: (formData) => formData?.additionalData?.eventPrice,
    validator: () => true,
    message: "Event price is recommended",
  },
  eventImageUrl: {
    required: false,
    step: 2,
    field: "eventImageUrl",
    getValueFromData: (formData) => formData?.additionalData?.eventImageUrl,
    validator: () => true,
    message: "Event image is recommended",
  },
  // Ad Video / Demo Video removed from the Review checklist (2026-06-07):
  // their upload inputs were taken out of the event form, so listing them as
  // "optional field not set" flagged fields the admin can't actually set.
  // (The advideoUrl/demoVideoUrl data fields still exist for back-compat; they
  // just no longer appear in validation.)

  // Step 3: Sponsors
  sponsorData: {
    required: true,
    step: 3,
    field: "sponsorData",
    validator: (v) => Array.isArray(v) && v.length >= 1,
    message: "At least one sponsor is required",
  },

  // Step 4: Template
  templateData: {
    required: true,
    step: 2,
    field: "templateData",
    validator: (v) => v && typeof v === "string" && v.trim() !== "" && !v.startsWith("blob:"),
    message: "Template image is required and must be fully uploaded",
  },

  // Combined caption-budget check — Instagram caps published caption at 2200 chars.
  // Item 7 (2026-06-04): Event Referral Caption (eventCaption2) is no longer in
  // the IG caption, so it's excluded. We sum Participation post caption +
  // hashtag block + NNexus-code block and HARD-FAIL if it exceeds 2200 OR the
  // participation caption exceeds its 1750-char cap.
  captionBudget: {
    required: true,
    step: 2,
    field: "captionBudget",
    getValueFromData: (formData) => ({
      cap1: formData?.additionalData?.participationPost?.caption1 || "",
      tags: formData?.additionalData?.participationPost?.hashtags || [],
    }),
    validator: (v) => {
      const b = estimateCaptionBudget({
        eventCaption1: v?.cap1 || "",
        hashtags: v?.tags || [],
      });
      // isOver now also trips when caption1 > 1750 (CAPTION1_MAX).
      return !b.isOver;
    },
    message: `Participation post caption is too long — keep it under 1750 characters (Instagram's total caption limit is ${MAX_CAPTION_LENGTH}).`,
  },
};

/**
 * Media validation for long events (Step 5)
 * Returns { hasAny, missingOverrideToggle, anyUserOverridable }
 *   hasAny — at least one media item (image/video/music) is configured
 *   missingOverrideToggle — items that don't have `default` explicitly set
 *     (legacy/stale data — caller should show a warning so admin re-saves)
 *   anyUserOverridable — at least one image OR video has default=true so
 *     the user can publish a reel/post for this event
 */
const validateMedia = (mediaDraft, mediaRef) => {
  const refPayload = mediaRef?.current?.getPayload?.();
  const draft = refPayload || {
    image_data: JSON.stringify(mediaDraft?.imageData || []),
    video_data: JSON.stringify(mediaDraft?.videoData || []),
    music_data: JSON.stringify(mediaDraft?.musicData || []),
  };

  const parse = (s) => {
    try {
      const arr = JSON.parse(s || "[]");
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  const images = parse(draft.image_data);
  const videos = parse(draft.video_data);
  const music = parse(draft.music_data);

  const hasAny = images.length > 0 || videos.length > 0 || music.length > 0;

  // Items that have a URL but no explicit default flag — happens for legacy
  // events saved before the Override-by-user toggle existed. The user app
  // treats `default=undefined` as locked, so admin should re-save to make
  // the value explicit.
  const missingOverrideToggle = [...images, ...videos, ...music].some(
    (it) =>
      it &&
      typeof it === "object" &&
      typeof it.url === "string" &&
      it.url.trim() !== "" &&
      typeof it.default !== "boolean"
  );

  // At least one image OR video must be user-overridable for the user app
  // to allow publishing — otherwise the reel is built purely from admin's
  // locked media and the participant has nothing to contribute.
  const anyUserOverridable = [...images, ...videos].some(
    (it) => it && typeof it === "object" && it.default === true
  );

  return { hasAny, missingOverrideToggle, anyUserOverridable };
};

/**
 * Custom hook for event validation
 * @param {Object} formData - The form data object
 * @param {Object} mediaDraft - Media draft data for long events
 * @param {React.RefObject} mediaRef - Reference to media component
 * @param {boolean} isLong - Whether it's a long event
 * @returns {Object} Validation utilities
 */
export const useEventValidation = (formData, mediaDraft, mediaRef, isLong) => {
  /**
   * Get validation status for a specific field
   */
  const getFieldValidation = useCallback(
    (fieldName) => {
      const rule = VALIDATION_RULES[fieldName];
      if (!rule) return { isValid: true, message: null };

      const value = rule.getValueFromData
        ? rule.getValueFromData(formData)
        : formData?.[fieldName];

      const isValid = rule.validator(value);

      return {
        isValid,
        isEmpty: !value || (typeof value === "string" && !value.trim()) || (Array.isArray(value) && value.length === 0),
        required: rule.required,
        message: rule.message,
        step: rule.step,
        field: rule.field,
      };
    },
    [formData]
  );

  /**
   * Get all validation issues
   */
  const getAllValidationIssues = useCallback(() => {
    const issues = [];

    Object.keys(VALIDATION_RULES).forEach((fieldName) => {
      const validation = getFieldValidation(fieldName);
      if (!validation.isValid && validation.required) {
        issues.push({
          field: validation.field,
          step: validation.step,
          message: validation.message,
          severity: "critical", // Required field
        });
      } else if (validation.isEmpty && !validation.required) {
        // Optional but empty - could add as warning if needed
        // issues.push({
        //   field: validation.field,
        //   step: validation.step,
        //   message: validation.message,
        //   severity: "warning",
        // });
      }
    });

    // Validate media for long events
    if (isLong) {
      const mediaState = validateMedia(mediaDraft, mediaRef);
      if (!mediaState.hasAny) {
        issues.push({
          field: "mediaRequired",
          step: 4,
          message: "At least one image, video, or music is required",
          severity: "critical",
        });
      } else {
        if (!mediaState.anyUserOverridable) {
          issues.push({
            field: "mediaOverride",
            step: 4,
            message:
              "Enable 'Override by user' on at least one image or video — otherwise participants cannot publish a post for this event.",
            severity: "critical",
          });
        }
        if (mediaState.missingOverrideToggle) {
          issues.push({
            field: "mediaOverride",
            step: 4,
            message:
              "Some media items are missing the 'Override by user' toggle (legacy data). Open each image/video/music item and toggle the switch once, then save to make the setting explicit.",
            severity: "warning",
          });
        }
      }
    }

    return issues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mediaDraft, mediaRef, isLong, getFieldValidation]);

  /**
   * Get validation issues for a specific step
   */
  const getStepValidationIssues = useCallback(
    (stepNumber) => {
      return getAllValidationIssues().filter((issue) => issue.step === stepNumber);
    },
    [getAllValidationIssues]
  );

  /**
   * Check if a step is valid
   */
  const isStepValid = useCallback(
    (stepNumber) => {
      const issues = getStepValidationIssues(stepNumber);
      return issues.filter((i) => i.severity === "critical").length === 0;
    },
    [getStepValidationIssues]
  );

  /**
   * Get CSS class for field highlighting
   */
  const getFieldHighlightClass = useCallback(
    (fieldName, baseClass = "") => {
      const validation = getFieldValidation(fieldName);
      if (!validation.isValid && validation.required) {
        return `${baseClass} ring-2 ring-yellow-400 bg-yellow-50`;
      }
      if (validation.isEmpty && !validation.required) {
        return `${baseClass} ring-1 ring-yellow-200 bg-yellow-25`;
      }
      return baseClass;
    },
    [getFieldValidation]
  );

  /**
   * Check if form can be submitted (for publish mode)
   */
  const canPublish = useMemo(() => {
    const issues = getAllValidationIssues();
    return issues.filter((i) => i.severity === "critical").length === 0;
  }, [getAllValidationIssues]);

  /**
   * Get summary of validation status
   */
  const validationSummary = useMemo(() => {
    const issues = getAllValidationIssues();
    const criticalCount = issues.filter((i) => i.severity === "critical").length;
    const warningCount = issues.filter((i) => i.severity === "warning").length;

    return {
      totalIssues: issues.length,
      criticalCount,
      warningCount,
      canPublish: criticalCount === 0,
      issues,
    };
  }, [getAllValidationIssues]);

  return {
    getFieldValidation,
    getAllValidationIssues,
    getStepValidationIssues,
    isStepValid,
    getFieldHighlightClass,
    canPublish,
    validationSummary,
    VALIDATION_RULES,
  };
};

export default useEventValidation;
